import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';

/**
 * The coupon code, as a click-to-copy chip.
 *
 * Copying is the thing a director does most with a code — it goes into an email or a flyer — so it
 * is one click from the list rather than something to select by hand out of a table cell.
 *
 * `navigator.clipboard` needs a secure context, so there is a textarea fallback for plain http
 * (which the dev server runs on). Both paths report success the same way; the chip has no failure
 * state because there is nothing useful to say — the code is right there to select manually.
 */
const CodeChip = ({ code, onCopied }) => {
    const [copied, setCopied] = useState(false);

    const copy = async event => {
        // The whole row is a click target that opens the coupon; copying must not also open it.
        event.stopPropagation();

        try {
            await navigator.clipboard.writeText(code);
        } catch (err) {
            const helper = document.createElement('textarea');
            helper.value = code;
            document.body.appendChild(helper);
            helper.select();
            document.execCommand('copy');
            document.body.removeChild(helper);
        }

        setCopied(true);
        if (onCopied) onCopied(code);
        window.setTimeout(() => setCopied(false), 1500);
    };

    return (
        <button
            type="button"
            onClick={copy}
            title={`Copy ${code}`}
            aria-label={`Copy coupon code ${code}`}
            className="cpn-group cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-md cpn-bg-neutral-100 cpn-px-2 cpn-py-0.5 cpn-font-mono cpn-text-[12px] cpn-font-medium cpn-text-neutral-700 cpn-transition hover:cpn-bg-brand-100 hover:cpn-text-brand-800 focus:cpn-outline-none focus-visible:cpn-ring-2 focus-visible:cpn-ring-brand-300"
        >
            {code}
            {copied ? (
                <Check className="cpn-h-3 cpn-w-3 cpn-text-emerald-600" aria-hidden="true" />
            ) : (
                <Copy
                    className="cpn-h-3 cpn-w-3 cpn-opacity-40 cpn-transition group-hover:cpn-opacity-100"
                    aria-hidden="true"
                />
            )}
            <span className="cpn-sr-only" role="status">
                {copied ? 'Copied' : ''}
            </span>
        </button>
    );
};

export default CodeChip;
